// client/src/reservations/components/WaitlistOptIn.jsx
import React, { useMemo, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';

export default function WaitlistOptIn({
  value,
  onChange,
  unavailableTimes = [],      // array<'HH:MM'>
  minDate,                    // 'YYYY-MM-DD'
  maxDate,                    // 'YYYY-MM-DD'
  includeDates = [],          // optional whitelist of dates
}) {
  // value shape now supports: { earlier: boolean, specific_times: string[], date?: 'YYYY-MM-DD' }
  const chosen = Array.isArray(value?.specific_times) ? value.specific_times : [];
  const [pendingTime, setPendingTime] = useState('');

  const remainingOptions = useMemo(() => {
    const chosenSet = new Set(chosen);
    return (unavailableTimes || [])
      .filter(Boolean)
      .filter(t => !chosenSet.has(t))
      .sort();
  }, [unavailableTimes, chosen]);

  function addSelectedTime() {
    if (!pendingTime) return;
    if (chosen.length >= 3) return;
    if (chosen.includes(pendingTime)) return;
    onChange({ ...value, specific_times: [...chosen, pendingTime] });
    setPendingTime('');
  }

  function removeTime(i) {
    const next = [...chosen];
    next.splice(i, 1);
    onChange({ ...value, specific_times: next });
  }

  return (
    <div className="border rounded-xl p-3 mt-3">
      <div className="font-medium mb-2">Join waitlist</div>

      {/* Date scope for the alert */}
      <div className="grid gap-3 sm:grid-cols-2 mb-3">
        <div>
          <label className="block text-xs text-gray-600">Date for alerts</label>
          <IsoDatePicker
            value={value?.date || ''}
            onChange={(iso) => onChange({ ...value, date: iso })}
            minDate={minDate}
            maxDate={maxDate}
            includeDates={includeDates}
            includeDatesStrict={!!includeDates?.length}
            isClearable
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600">Specific time</label>
          <div className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-1 w-40"
              value={pendingTime}
              onChange={e => setPendingTime(e.target.value)}
              disabled={remainingOptions.length === 0 || chosen.length >= 3}
            >
              <option value="">
                {remainingOptions.length ? 'Select time' : 'No unavailable times'}
              </option>
              {remainingOptions.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addSelectedTime}
              disabled={!pendingTime || chosen.length >= 3}
              className="text-xs rounded px-2 py-1 border disabled:opacity-40"
            >
              + Add time
            </button>
          </div>

          {/* Selected times */}
          {!!chosen.length && (
            <div className="mt-2 flex flex-wrap gap-2">
              {chosen.map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border rounded"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTime(i)}
                    className="text-red-600"
                    aria-label={`Remove ${t}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!value?.earlier}
          onChange={e => onChange({ ...value, earlier: e.target.checked })}
        />
        <span className="text-sm">Notify me if an earlier slot opens</span>
      </label>

      <div className="text-xs text-gray-500 mt-2">
        We’ll only send alerts for the date you select. You can add up to 3 specific times from the
        currently <em>unavailable</em> slots.
      </div>
    </div>
  );
}
